import React from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import Checkbox from '@mui/material/Checkbox';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import BrokenImageOutlinedIcon from '@mui/icons-material/BrokenImageOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

import { runningSinceLabel } from './staticSort';
import formatEnumLabel from './formatEnumLabel';
import { STATIC_STATUS_COLOR, isStaticAdBusy, staticStatusLabel } from './staticStatus';
import { StaticAdSummary, StaticPipelineRow } from './types';

const STAGE_LABEL: Record<string, string> = {
  prepare: 'fetching image',
  analyze: 'analyzing',
  extract: 'extracting',
};

interface StaticAdCardProps {
  selected?: boolean;
  /** Omitted where selection isn't offered — the checkbox then isn't rendered. */
  onToggleSelect?: (staticAdId: string) => void;
  ad: StaticAdSummary;
  status?: StaticPipelineRow;
  onOpen: (staticAdId: string) => void;
}

/**
 * One tile in the library grid.
 *
 * Thumbnail-first: the card renders the 320px derivative, never the full image.
 * A grid of 60 full-size ad creatives is tens of megabytes for something the
 * user is only scanning.
 */
export default function StaticAdCard({ ad, status, onOpen, selected, onToggleSelect }: StaticAdCardProps) {
  const pipelineStatus = status?.pipelineStatus ?? ad.pipelineStatus;
  const stage = status?.pipelineStage ?? ad.pipelineStage;
  const errorMessage = status?.errorMessage ?? ad.errorMessage;

  const isRunning = isStaticAdBusy(pipelineStatus);
  const runningLabel = runningSinceLabel(ad.startDate);
  const isFailed = pipelineStatus === 'FAILED';

  return (
    <Card
      variant="outlined"
      sx={{
        height: '100%', display: 'flex', flexDirection: 'column', position: 'relative',
        // Selection has to be visible on the card itself: in a grid the only
        // other cue is a checkbox 200px away in the corner of a tile.
        borderColor: selected ? 'primary.main' : undefined,
        borderWidth: selected ? 2 : 1,
      }}
    >
      {onToggleSelect && (
        <Checkbox
          size="small"
          checked={selected}
          onChange={() => onToggleSelect(ad.staticAdId)}
          // Sits above the action area rather than inside it, so ticking a card
          // doesn't also open it.
          onClick={e => e.stopPropagation()}
          inputProps={{ 'aria-label': `Select ${ad.advertiserName || ad.pageName || 'ad'}` }}
          sx={{
            position: 'absolute', top: 2, left: 2, zIndex: 2,
            bgcolor: 'rgba(255,255,255,0.85)', borderRadius: 1, p: 0.25,
            '&:hover': { bgcolor: 'rgba(255,255,255,0.95)' },
          }}
        />
      )}
      {/* The card has two jobs, so it has two click targets: the image opens
          the ad, the copy underneath selects it. One action area covering both
          made every attempt to select something open it instead. */}
      <CardActionArea
        onClick={() => onOpen(ad.staticAdId)}
        aria-label={`Open ${ad.advertiserName || ad.pageName || 'ad'}`}
        sx={{ flexGrow: 0 }}
      >
        <Box sx={{
          position: 'relative',
          width: '100%',
          aspectRatio: '1 / 1',
          bgcolor: 'grey.100',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
        }}>
          {ad.thumbnailUrl ? (
            <Box
              component="img"
              src={ad.thumbnailUrl}
              alt={ad.headline || ad.pageName || 'Static ad'}
              loading="lazy"
              sx={{ width: '100%', height: '100%', objectFit: 'contain' }}
            />
          ) : (
            // No thumbnail yet: either still being fetched, or the fetch failed.
            // Showing which is the difference between "wait" and "do something".
            <Stack alignItems="center" spacing={1} sx={{ color: 'text.disabled' }}>
              {isRunning
                ? <CircularProgress size={22} />
                : <BrokenImageOutlinedIcon sx={{ fontSize: 36 }} />}
              <Typography variant="caption">
                {isRunning ? (STAGE_LABEL[stage || ''] || 'queued') : 'no image'}
              </Typography>
            </Stack>
          )}

          {ad.isCarousel && (
            <Chip
              size="small"
              label={`Card ${ad.cardOrdinal + 1}/${ad.cardCount}`}
              sx={{ position: 'absolute', top: 6, left: 6, height: 20, fontSize: '0.65rem' }}
            />
          )}
          {/* One creative, several ads. Advertisers routinely run the same
              image as many separate ads; without this the grid looked like it
              had fetched the same thing over and over. */}
          {(ad.duplicateAdCount || 1) > 1 && (
            <Tooltip title={`${ad.duplicateAdCount} separate ads run this creative`}>
              <Chip
                size="small"
                color="info"
                label={`×${ad.duplicateAdCount}`}
                sx={{
                  position: 'absolute', bottom: 6, left: 6, height: 20, fontSize: '0.65rem',
                  fontWeight: 600,
                }}
              />
            </Tooltip>
          )}
          {ad.overallGrade != null && (
            <Chip
              size="small"
              color={ad.overallGrade >= 4 ? 'success' : 'default'}
              label={`${ad.overallGrade}/5`}
              sx={{ position: 'absolute', top: 6, right: 6, height: 20, fontSize: '0.65rem' }}
            />
          )}
          {isFailed && (
            <Tooltip title={errorMessage || 'Processing failed'}>
              <ErrorOutlineIcon
                color="error"
                sx={{ position: 'absolute', bottom: 6, right: 6, fontSize: 22 }}
              />
            </Tooltip>
          )}
        </Box>
      </CardActionArea>

      <CardActionArea
        onClick={() => onToggleSelect?.(ad.staticAdId)}
        disabled={!onToggleSelect}
        aria-label={`${selected ? 'Deselect' : 'Select'} ${ad.advertiserName || ad.pageName || 'ad'}`}
        sx={{
          flexGrow: 1, alignItems: 'flex-start',
          // Tinted while selected so the body reads as the thing that was
          // clicked, rather than the selection living only in a far corner.
          bgcolor: selected ? 'action.selected' : undefined,
        }}
      >
        <Box sx={{ p: 1.25, width: '100%' }}>
          <Typography variant="subtitle2" noWrap title={ad.pageName || ''}>
            {ad.advertiserName || ad.pageName || 'Unknown advertiser'}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            minHeight: '2.4em',
          }}>
            {ad.headline || ad.ocrText || '—'}
          </Typography>
          {/* The facts you sort by, visible on the card you sorted — a grid
              ordered by "started running" is unreadable if the card never says
              how long anything has been running. */}
          <Stack direction="row" spacing={0.75} sx={{ mt: 0.5, alignItems: 'center', flexWrap: 'wrap' }}>
            {runningLabel && (
              <Typography variant="caption" color="text.secondary" noWrap>{runningLabel}</Typography>
            )}
            {ad.keyword && (
              <Tooltip title={`Fetched for "${ad.keyword}"`}>
                <Typography variant="caption" color="text.disabled" noWrap sx={{ maxWidth: 120 }}>
                  · {ad.keyword}
                </Typography>
              </Tooltip>
            )}
          </Stack>

          <Stack direction="row" spacing={0.5} sx={{ mt: 0.75, flexWrap: 'wrap', gap: 0.5 }}>
            {/* Anything not yet analyzed says so plainly. Without this the only
                difference between an analyzed and an un-analyzed ad is the
                absence of chips, which reads as "nothing found". */}
            {pipelineStatus !== 'DONE' && (
              <Chip
                size="small"
                color={STATIC_STATUS_COLOR[pipelineStatus] || 'default'}
                variant={pipelineStatus === 'NEW' ? 'outlined' : 'filled'}
                label={staticStatusLabel(pipelineStatus)}
                sx={{ height: 20, fontSize: '0.65rem' }}
              />
            )}
            {ad.staticFormat && (
              <Chip size="small" variant="outlined" label={formatEnumLabel(ad.staticFormat)} sx={{ height: 20, fontSize: '0.65rem' }} />
            )}
            {ad.visualHook && (
              <Chip size="small" variant="outlined" label={formatEnumLabel(ad.visualHook)} sx={{ height: 20, fontSize: '0.65rem' }} />
            )}
            {ad.aspectRatio && (
              <Chip size="small" variant="outlined" label={ad.aspectRatio} sx={{ height: 20, fontSize: '0.65rem' }} />
            )}
            {/* Tags last and visually distinct: these are yours, everything
                above them was written by the model or by Meta. */}
            {(ad.tags || []).map(tag => (
              <Chip
                key={tag}
                size="small"
                color="secondary"
                variant="outlined"
                label={tag}
                sx={{ height: 20, fontSize: '0.65rem' }}
              />
            ))}
          </Stack>
        </Box>
      </CardActionArea>
    </Card>
  );
}
